/**
 * meta/cosmetics-catalog.js — what the goose can wear, and wearing it (GDD 3.4).
 *
 * `avatar/cosmetics.js` owns the five attach points and never changes; this
 * module supplies the catalog it was written against and drives equip/unequip.
 *
 * Ownership is authoritative in the save, not in the avatar. So equip() writes
 * `save.cosmetics.equipped` FIRST and then asks the avatar to mount, with
 * `persist: false` so the write does not happen twice. That ordering matters:
 * the meta layer runs before the goose exists during boot, and a wardrobe
 * choice must survive a session where the avatar was never built.
 *
 * @module meta/cosmetics-catalog
 */

import { COSMETIC_CATALOG, META_EVENTS } from '../constants.js';
import { save } from '../core/save.js';
import { bus } from '../core/events.js';
import { logCall } from '../core/testmode.js';
import { mat } from '../assets3d/factory.js';
import { cosmetics as avatarCosmetics, SLOTS } from '../avatar/cosmetics.js';
import { SKIN_PALETTES, isSkin } from './cosmetic-models.js';

const BY_ID = Object.fromEntries(COSMETIC_CATALOG.map((c) => [c.id, c]));

/** Original goose materials, captured once so a skin can be taken off again. */
let originalMaterials = null;
/** Which skin is currently painted on, so we do not repaint every frame. */
let activeSkin = null;

/**
 * Every mesh of the goose that a skin recolours, tagged by role.
 * @returns {Array<{ mesh: THREE.Mesh, role: 'body'|'beak'|'wing' }>}
 */
function skinnableParts() {
  const goose = avatarCosmetics.goose;
  if (!goose) return [];
  const parts = goose.userData.parts || {};
  const out = [];
  for (const key of ['body', 'head', 'neck', 'tail']) {
    if (parts[key]) out.push({ mesh: parts[key], role: 'body' });
  }
  if (parts.beak) out.push({ mesh: parts.beak, role: 'beak' });
  for (const name of ['wing-left', 'wing-right']) {
    const wing = goose.getObjectByName(name);
    if (wing) out.push({ mesh: wing, role: 'wing' });
  }
  return out;
}

/**
 * Paint (or strip) a goose skin. Skins are a material swap rather than a
 * mounted mesh, which is why they bypass the attach-point system entirely.
 * @param {string|null} id Null restores the default plumage.
 */
function applySkin(id) {
  const parts = skinnableParts();
  if (!parts.length) {
    // The goose is not built yet — remember the intent; applyFromSave() on the
    // next init replays it through equip().
    activeSkin = id;
    return;
  }

  if (!originalMaterials) {
    originalMaterials = new Map();
    for (const { mesh } of parts) originalMaterials.set(mesh, mesh.material);
  }

  if (!id || !SKIN_PALETTES[id]) {
    for (const { mesh } of parts) {
      const original = originalMaterials.get(mesh);
      if (original) mesh.material = original;
    }
    activeSkin = null;
    return;
  }

  const p = SKIN_PALETTES[id];
  for (const { mesh, role } of parts) {
    const color = role === 'beak' ? p.beak : (role === 'wing' ? p.wing : p.body);
    mesh.material = mat(color, { rough: p.rough, metal: p.metal });
  }
  activeSkin = id;
}

export const cosmetics = {
  /**
   * The catalog, annotated with what the player has and is wearing.
   * @returns {Array<object>}
   */
  catalog() {
    logCall('meta', 'cosmetics.catalog', []);
    const owned = save.data.cosmetics.owned || [];
    const equipped = save.data.cosmetics.equipped || {};
    return COSMETIC_CATALOG.map((c) => ({
      ...c,
      owned: owned.includes(c.id),
      equipped: equipped[c.slot] === c.id,
      isSkin: isSkin(c.id),
    }));
  },

  /** @returns {Array<object>} Only what the player actually has. */
  ownedList() {
    return this.catalog().filter((c) => c.owned);
  },

  /** @returns {string[]} */
  owned() {
    return [...(save.data.cosmetics.owned || [])];
  },

  /** @param {string} id @returns {boolean} */
  owns(id) {
    return (save.data.cosmetics.owned || []).includes(id);
  },

  /** @param {string} id @returns {object|null} */
  def(id) {
    return BY_ID[id] || null;
  },

  /** @returns {Record<string, string|null>} */
  equipped() {
    return { ...save.data.cosmetics.equipped };
  },

  /**
   * Grant a cosmetic. Used by set completion and the day-30 login payout.
   * @param {string} id
   * @returns {boolean} False if it was already owned.
   */
  unlock(id) {
    return avatarCosmetics.unlock(id);
  },

  /**
   * Wear a cosmetic (or clear the slot with `null`).
   *
   * Refuses items the player does not own or that do not belong in `slot` —
   * the wardrobe UI only offers valid combinations, but `__cc` is a public
   * surface and a test can ask for anything.
   *
   * @param {'head'|'body'|'beak'|'feet'|'aura'} slot
   * @param {string|null} id
   * @returns {boolean} Whether the request was accepted.
   */
  equip(slot, id) {
    logCall('meta', 'cosmetics.equip', [slot, id]);
    if (!SLOTS.includes(slot)) return false;

    if (id) {
      const def = BY_ID[id];
      if (!def || def.slot !== slot) return false;
      if (!this.owns(id)) return false;
    }

    save.data.cosmetics.equipped[slot] = id ?? null;
    save.markDirty();

    // Mount the mesh (skins mount an empty group by design), then repaint.
    avatarCosmetics.equip(slot, id ?? null, { persist: false });
    if (slot === 'body') applySkin(id && isSkin(id) ? id : null);

    bus.emit(META_EVENTS.COSMETIC_EQUIPPED, { slot, id: id ?? null });
    return true;
  },

  /** @param {'head'|'body'|'beak'|'feet'|'aura'} slot @returns {boolean} */
  unequip(slot) {
    return this.equip(slot, null);
  },

  /**
   * Re-apply everything in the save to a freshly built goose. Called once the
   * avatar exists, because a skin equipped before that had nothing to paint.
   */
  refresh() {
    const equipped = save.data.cosmetics.equipped || {};
    for (const slot of SLOTS) {
      avatarCosmetics.equip(slot, equipped[slot] ?? null, { persist: false });
    }
    const body = equipped.body;
    applySkin(body && isSkin(body) ? body : null);
  },

  /** @returns {string|null} The skin currently painted on the goose. */
  activeSkin() {
    return activeSkin;
  },

  /** @returns {Array<string>} The five slot names, in UI order. */
  slots() {
    return [...SLOTS];
  },
};

export default cosmetics;
